import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Phone, Clock, ChevronDown, Edit2, ShieldCheck, X, Hash } from 'lucide-react';
import { STATUS_OPTIONS, STATUS_COLORS } from '../data/mockData';

const BASE_PRICE = 5;

const OccMemberModal = ({ onClose, onSuccess, userId }) => {
  const [memberNumber, setMemberNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!memberNumber.trim()) {
      setError('Por favor insira o seu número de membro');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/save-occ-member-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, member_number: memberNumber.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Erro ao guardar número de membro');
        return;
      }

      onSuccess();
    } catch {
      setError('Erro de ligação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Verificação OCC</h2>
            <p className="text-sm text-gray-500">Confirme que é contabilista certificado</p>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          Para desbloquear pedidos de contacto, introduza o seu número de membro da{' '}
          <strong>Ordem dos Contabilistas Certificados</strong>. Este número será utilizado para verificação da sua certificação.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de membro OCC
            </label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={memberNumber}
                onChange={(e) => setMemberNumber(e.target.value)}
                placeholder="Ex: 12345"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            {error && (
              <p className="mt-1 text-xs text-red-600">{error}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-gradient-primary text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                A guardar...
              </span>
            ) : (
              'Confirmar e continuar'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};


const daysSince = (date) => (Date.now() - new Date(date)) / (1000 * 60 * 60 * 24);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [showOccModal, setShowOccModal] = useState(false);
  const [instantNotif, setInstantNotif] = useState(false);
  const [savingInstantNotif, setSavingInstantNotif] = useState(false);
  const [pendingClaimLead, setPendingClaimLead] = useState(null);

  useEffect(() => {
    loadLeads();
    if (user) setInstantNotif(user.instant_lead_notifications ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleInstantNotifToggle = async (newValue) => {
    setSavingInstantNotif(true);
    setInstantNotif(newValue);
    try {
      const { error } = await supabase.auth.updateUser({ data: { instant_lead_notifications: newValue } });
      if (error) throw error;
      await supabase.auth.refreshSession();
    } catch {
      setInstantNotif(!newValue);
    } finally {
      setSavingInstantNotif(false);
    }
  };


  const loadLeads = async () => {
    try {
      const { data: unclaimedData, error: unclaimedError } = await supabase
        .from('leads_masked')
        .select('id, created_at, summary, is_claimed, claimed_by, claimed_at, price, status, source')
        .eq('is_claimed', false)
        .order('created_at', { ascending: false });

      if (unclaimedError) throw unclaimedError;

      const { data: claimedData, error: claimedError } = await supabase
        .from('Leads')
        .select('*')
        .eq('claimed_by', user.id)
        .order('created_at', { ascending: false });

      if (claimedError) throw claimedError;

      setLeads([...(unclaimedData || []), ...(claimedData || [])]);

    } catch (error) {
      console.error('Error loading leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimLead = async (lead) => {
    if (!user?.occMemberNumberSubmitted && claimedLeads.length === 0) {
      setPendingClaimLead(lead);
      setShowOccModal(true);
      return;
    }

    if (!user?.hasPaymentMethod) {
      const goToBilling = window.confirm(
        'Precisa de adicionar um método de pagamento antes de desbloquear leads.\n\nIr para Billing agora?'
      );
      if (goToBilling) navigate('/billing');
      return;
    }

    const price = lead.price ?? BASE_PRICE;
    const confirmed = window.confirm(
      '🔓 Desbloquear esta lead?\n\n' +
      `Serão cobrados ${price}€ ao seu método de pagamento.\n` +
      'Os dados de contacto estarão disponíveis após o pagamento.'
    );

    if (!confirmed) return;

    setClaiming(lead.id);

    try {
      const response = await fetch('/api/claim-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id, user_id: user.id })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'OCC_MEMBER_NUMBER_NOT_SUBMITTED') {
          setPendingClaimLead(lead);
          setShowOccModal(true);
        } else if (data.code === 'ALREADY_CLAIMED') {
          alert('Esta lead já foi desbloqueada por outro utilizador.');
          loadLeads();
        } else if (data.code === 'NO_PAYMENT_METHOD') {
          const goToBilling = window.confirm(
            'Precisa de adicionar um método de pagamento.\n\nIr para Billing agora?'
          );
          if (goToBilling) navigate('/billing');
        } else if (data.code === 'PAYMENT_FAILED') {
          alert('Falha no pagamento: ' + data.error + '\n\nVerifique o seu método de pagamento.');
        } else {
          throw new Error(data.error || 'Failed to claim lead');
        }
        return;
      }

      alert('✅ Lead desbloqueada com sucesso!');
      setActiveTab('claimed');
      loadLeads();

    } catch (error) {
      console.error('Claim error:', error);
      alert('Erro ao desbloquear lead: ' + error.message);
    } finally {
      setClaiming(null);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { error } = await supabase
        .from('Leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(leads.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Erro ao atualizar status');
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    try {
      const { error } = await supabase
        .from('Leads')
        .update({ notes })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(leads.map(lead =>
        lead.id === leadId ? { ...lead, notes } : lead
      ));
      setEditingNotes(null);
      setNoteText('');
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Erro ao guardar notas');
    }
  };

  const availableLeads = leads.filter(lead => !lead.is_claimed);
  const claimedLeads = leads.filter(lead => lead.claimed_by === user?.id);
  const filteredClaimedLeads = statusFilter
    ? claimedLeads.filter(lead => lead.status === statusFilter)
    : claimedLeads;

  if (loading) {
    return (
      <div className="w-full px-4 flex items-center justify-center h-full">
        <div className="text-gray-600">A carregar leads...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {showOccModal && (
        <OccMemberModal
          userId={user?.id}
          onClose={() => { setShowOccModal(false); setPendingClaimLead(null); }}
          onSuccess={async () => {
            setShowOccModal(false);
            await supabase.auth.refreshSession();
            if (pendingClaimLead) {
              const lead = pendingClaimLead;
              setPendingClaimLead(null);
              handleClaimLead(lead);
            }
          }}
        />
      )}



      {/* Header com métricas */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Olá {user?.name?.split(' ')[0]}!
        </h1>

        <div className="inline-block bg-accent border border-primary rounded-full px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{availableLeads.length}</div>
              <div className="text-xs text-primary font-medium">Disponíveis</div>
            </div>
            <div className="w-px h-8 bg-primary/30" />
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{claimedLeads.length}</div>
              <div className="text-xs text-primary font-medium">Leads Desbloqueadas</div>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Informativo do Modelo de Pricing */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Anúncio
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-2">
                Para garantir a viabilidade do negócio, tivemos de ajustar ligeiramente o nosso modelo de pricing. <strong className="text-gray-900">O cliente continua a pagar apenas por lead, mas o valor poderá oscilar de acordo com as movimentações dos nossos custos de aquisição.</strong>
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                A política de reembolso foi igualmente atualizada: <strong className="text-gray-900">a possibilidade de pedido de reembolso de um pedido de contacto deixa de estar disponível.</strong> Tal como qualquer profissional independente, os custos de aquisição fazem parte do exercício da atividade e a ambiguidade na legitimidade dos pedidos tornava este modelo inviável.
              </p>
              <div className="flex items-center gap-3 flex-wrap" style={{ display: 'none' }}>
                <p className="text-sm text-gray-700">
                  Conheça-nos melhor e receba atualizações sobre a evolução da plataforma:
                </p>
                <a
                  href="https://chat.whatsapp.com/Fg4sAD0tBVyCV7orvYVErY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm whitespace-nowrap"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Comunidade fiscobem.pt
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notificação Instantânea */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-gray-900">Notificação imediata de nova lead</p>
            <p className="text-xs text-gray-500 mt-0.5">Receber email no momento em que entra um novo pedido de contacto</p>
          </div>
          <button
            onClick={() => handleInstantNotifToggle(!instantNotif)}
            disabled={savingInstantNotif}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 ${instantNotif ? 'bg-primary' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${instantNotif ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('available')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'available'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pedidos de contacto por desbloquear ({availableLeads.length})
            </button>
            <button
              onClick={() => setActiveTab('claimed')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'claimed'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Os meus pedidos de contacto ({claimedLeads.length})
            </button>
          </nav>
        </div>
      </div>

      {/* CONTEÚDO DO TAB */}
      {activeTab === 'available' ? (
        <div>
          {availableLeads.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-primary/10">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-gray-500">Nenhuma lead disponível de momento</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md divide-y divide-gray-200 border border-primary/10">
              {availableLeads.map((lead) => {
                const price = lead.price ?? BASE_PRICE;
                const isDiscounted = price < BASE_PRICE;
                return (
                  <div key={lead.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full whitespace-nowrap">
                          Bloqueada
                        </span>
                        {isDiscounted && (
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full whitespace-nowrap">
                            Preço reduzido
                          </span>
                        )}
                        {lead.source === 'website' && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full whitespace-nowrap">
                            Alta probabilidade de conversão
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(lead.created_at).toLocaleDateString('pt-PT')}
                        </span>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Pedido:</h3>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg break-words">
                          {lead.summary || 'Sem descrição disponível'}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        <div className="text-center sm:text-left">
                          {isDiscounted ? (
                            <div className="flex items-baseline gap-1">
                              <span className="text-lg text-gray-400 line-through">{BASE_PRICE}€</span>
                              <span className="text-2xl font-bold text-primary">{price}€</span>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-primary">{price}€</div>
                          )}
                          <div className="text-xs text-gray-500">por desbloquear</div>
                        </div>
                        <button
                          onClick={() => handleClaimLead(lead)}
                          disabled={claiming === lead.id}
                          className="w-full sm:w-auto px-6 py-3 bg-gradient-primary text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {claiming === lead.id ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              A processar...
                            </span>
                          ) : (
                            'Desbloquear Contacto'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Filtros */}
          <div className="bg-white rounded-xl shadow-md p-6 border-b border-gray-200 mb-6 border border-primary/10">
            <div className="flex items-center space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Todos os Status</option>
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter('')}
                  className="text-primary hover:text-primary-dark text-sm font-medium"
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          </div>

          {filteredClaimedLeads.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-primary/10">
              <div className="text-4xl mb-4">📭</div>
              <p className="text-gray-500 mb-4">
                {claimedLeads.length === 0
                  ? 'Ainda não desbloqueou nenhuma lead'
                  : 'Nenhuma lead corresponde ao filtro'}
              </p>
              {claimedLeads.length === 0 && (
                <button
                  onClick={() => setActiveTab('available')}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Ver Pedidos de Contacto Disponíveis
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md divide-y divide-gray-200 border border-primary/10">
              {filteredClaimedLeads.map((lead) => (
                <div key={lead.id} className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-semibold text-gray-900 break-words">{lead.full_name}</span>
                        {lead.payment_status === 'paid' && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full whitespace-nowrap">
                            ✓ Pago
                          </span>
                        )}
                        {lead.source === 'website' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full whitespace-nowrap">
                            Probabilidade de conversão maior
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <a href={`tel:${lead.phone}`} className="hover:text-primary break-all">
                            {lead.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span>{new Date(lead.created_at).toLocaleDateString('pt-PT')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <select
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        className={`flex-1 sm:flex-initial px-3 py-2 text-xs font-medium rounded-full border-2 ${
                          STATUS_COLORS[lead.status] || 'border-gray-300 text-gray-700'
                        }`}
                      >
                        {STATUS_OPTIONS.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setSelectedLead(selectedLead === lead.id ? null : lead.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                      >
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 transition-transform ${
                            selectedLead === lead.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {selectedLead === lead.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">Notas</h4>
                          {!editingNotes && (
                            <button
                              onClick={() => {
                                setEditingNotes(lead.id);
                                setNoteText(lead.notes || '');
                              }}
                              className="text-xs text-primary hover:text-primary-dark flex items-center gap-1"
                            >
                              <Edit2 className="w-3 h-3" />
                              Editar
                            </button>
                          )}
                        </div>
                        {editingNotes === lead.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                              rows="3"
                              placeholder="Adicione notas sobre esta lead..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => updateLeadNotes(lead.id, noteText)}
                                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark"
                              >
                                Guardar
                              </button>
                              <button
                                onClick={() => {
                                  setEditingNotes(null);
                                  setNoteText('');
                                }}
                                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p
                            onClick={() => {
                              setEditingNotes(lead.id);
                              setNoteText(lead.notes || '');
                            }}
                            className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors break-words"
                          >
                            {lead.notes || 'Sem notas'}
                          </p>
                        )}
                      </div>

                      <div className="bg-accent p-4 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Pedido de Serviço</h4>
                        <p className="text-sm text-gray-700 break-words">{lead.summary || 'Sem detalhes'}</p>
                      </div>

                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
